/**
 * Konfigurator auf der Produktseite (Blöcke „Konfigurator: Schritt“ und „Konfigurator: Option“ in main-product).
 * Liquid rendert alle Schritte und Optionen; ohne JavaScript bleiben sie verborgen und der Kaufen-Button
 * legt nur das Hauptprodukt in den Warenkorb. Das Skript ergänzt:
 * - Bedingungen „Zeigen, wenn“: `schritt=option` (mehrere Werte mit |, mehrere Bedingungen mit Komma)
 * - Gesamtpreis am Preisblock (Set-Preis, wie ihn der Rabatt an der Kasse abzieht)
 * - Kacheln mit Varianten: Klick öffnet das Pop-up (<dialog>), die Kachel zeigt danach Größe und Preis
 * - Warenkorb-Zeilen für product.js: Hauptprodukt + gewählte Produkte. Teile aus Schritten mit einer Auswahl
 *   (Karten, Kacheln) gehören zum Set: versteckte Eigenschaft `_set`, Teile zusätzlich `_set_part`; cart-item.liquid
 *   gruppiert sie und entfernt sie nur mit dem Set. Zubehör (Häkchen) kommt als normaler Artikel dazu.
 * - Zubehör mit Varianten: Pop-up mit Variante und Anzahl
 * - Hinweise (`[data-config-hint]`) mit eigener Bedingung, Menge je Modul (`data-qty-from`: Anzahl aus einem anderen Schritt),
 *   Variante je Auswahl (`data-variant-map`, z. B. Halterung „2er Montage“ bei zwei Modulen)
 * - Schritt-Assistent (main-configurator, `data-config-wizard`): eine Seite je Schritt, Fortschritt, Set-Karte, Übersicht
 * Preise sind nur Anzeige. Was der Kunde zahlt, entscheidet Shopify an der Kasse (Produktpreis minus Rabatt).
 */
class SgConfigurator {
  constructor(product) {
    this.product = product;
    this.sectionId = product.dataset.section;
    this.steps = [...product.querySelectorAll('[data-config-step]')];
    this.format = new Intl.NumberFormat(document.documentElement.lang || 'de', { style: 'currency', currency: 'EUR' });

    product.addEventListener('change', (event) => {
      if (event.target.closest('[data-config-step]')) this.update();
    });
    product.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-config-dialog-open], [data-config-dialog-show]');
      if (opener) {
        const option = opener.closest('[data-config-option]');
        const input = option.querySelector('.config-option__input');
        // Klick auf ein angehaktes Zubehör nimmt es heraus (Standardaktion des Labels), dann kein Pop-up
        if (opener.matches('[data-config-dialog-open]') && input.type === 'checkbox' && input.checked) return;
        if (opener.matches('[data-config-dialog-show]')) input.checked = true;
        const dialog = option.querySelector('[data-config-dialog]');
        if (dialog && !dialog.open) dialog.showModal();
        if (opener.matches('[data-config-dialog-show]')) this.update();
        return;
      }
      const step = event.target.closest('[data-config-qty-change]');
      if (step) {
        const field = step.closest('[data-config-qty]').querySelector('[data-config-qty-input]');
        field.value = String(Math.min(Number(field.max) || 99, Math.max(1, (Number(field.value) || 1) + Number(step.dataset.configQtyChange))));
        this.update();
        return;
      }
      if (event.target.closest('[data-config-dialog-close]')) event.target.closest('dialog')?.close();
    });
    // Klick auf den abgedunkelten Hintergrund schließt das Pop-up
    product.querySelectorAll('[data-config-dialog]').forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    });
    document.addEventListener('variant:changed', () => this.renderPrice());
    // product.js ersetzt den Preisblock nach jedem Variantenwechsel, danach neu setzen
    product.addEventListener('product:rendered', () => this.renderPrice());
    this.update();
  }

  /* ---------- Auswahl und Bedingungen ---------- */

  // Bedingungen beziehen sich auf frühere Schritte, deshalb Schritt für Schritt von oben auswerten
  update() {
    const selection = new Map();
    this.selection = selection;
    this.steps.forEach((step) => {
      const visible = this.matches(step.dataset.showIf, selection);
      step.hidden = !visible;
      const options = [...step.querySelectorAll('[data-config-option]')];
      options.forEach((option) => {
        option.hidden = !visible || !this.matches(option.dataset.showIf, selection);
      });
      if (!visible) return;

      const usable = options.filter((option) => !option.hidden).map((option) => option.querySelector('.config-option__input'));
      if (step.dataset.layout !== 'checks' && !usable.some((input) => input.checked) && this.autoSelect(step, usable)) {
        const first = usable.find((input) => !input.disabled);
        if (first) first.checked = true;
      }
      options.filter((option) => option.dataset.variantMap).forEach((option) => this.applyVariantMap(option, selection));
      const chosen = usable.filter((input) => input.checked);
      selection.set(step.dataset.configStep, new Set(chosen.map((input) => input.value)));
      // Hinweise dürfen sich auf den eigenen Schritt beziehen (z. B. speicher=ohne)
      step.querySelectorAll('[data-config-hint]').forEach((hint) => {
        hint.hidden = !this.matches(hint.dataset.showIf, selection);
      });

      const value = step.querySelector('[data-config-value]');
      if (value) value.textContent = chosen.map((input) => input.dataset.label).join(', ');
      step.querySelectorAll('.config-option__variants').forEach((group) => {
        const label = group.querySelector('[data-config-variant-value]');
        const checked = group.querySelector('.chips__input:checked');
        if (label) label.textContent = checked ? checked.dataset.value : '';
        // Set-Vorteil hängt an der gewählten Variante
        const bonus = group.querySelector('[data-config-bonus]');
        const amount = checked ? Number(checked.dataset.bonus) || 0 : 0;
        if (bonus) {
          bonus.hidden = !amount;
          bonus.querySelector('[data-config-bonus-text]').textContent = bonus.dataset.template.replace('%amount%', this.format.format(amount / 100));
        }
        const option = group.closest('[data-config-option]');
        const count = this.count(option);
        const price = checked ? ((Number(checked.dataset.price) - Number(checked.dataset.bonus)) * count) / 100 : 0;
        const line = option.querySelector('[data-config-tile-line]');
        if (line && checked) line.textContent = `${checked.dataset.value} · + ${this.format.format(price).replace(',00', '')}`;
        const cardLine = option.querySelector('[data-config-card-line]');
        if (cardLine && checked) cardLine.textContent = `${count > 1 ? `${count}× ` : ''}${checked.dataset.value}`;
        const cardPrice = option.querySelector('[data-config-card-price]');
        if (cardPrice && checked) cardPrice.textContent = `+ ${this.format.format(price)}`;
      });
    });
    // Menge je Modul: Kartenpreis mit der Anzahl aus einem anderen Schritt (erst nach allen Schritten bekannt)
    this.product.querySelectorAll('[data-config-option][data-qty-from]').forEach((option) => {
      const input = option.querySelector('.config-option__input');
      const price = option.querySelector('[data-config-unit-price]');
      if (!price || !input.dataset.price) return;
      const total = ((Number(input.dataset.price) - Number(input.dataset.bonus)) * this.count(option)) / 100;
      price.textContent = `+ ${this.format.format(total)}`;
    });
    this.renderPrice();
    this.product.dispatchEvent(new CustomEvent('config:updated'));
  }

  // Ohne Auswahl die erste Option nehmen: im Konfigurator von main-product immer. Im Schritt-Assistenten wählt der Kunde
  // selbst; automatisch nur in Pop-up-Schritten (Vorauswahl der Vorlage) und wenn nur eine Option ohne Varianten bleibt
  // (z. B. „2 m Anschlusskabel, beim Glory dabei“).
  autoSelect(step, usable) {
    if (!this.product.hasAttribute('data-config-wizard')) return true;
    if (step.hasAttribute('data-wizard-popup-step')) return true;
    return usable.length === 1 && !usable[0].disabled && !usable[0].closest('[data-config-option]').querySelector('.config-option__variants');
  }

  // Variante nach der Auswahl eines früheren Schritts (z. B. Halterung „2er Montage“ bei zwei Modulen)
  applyVariantMap(option, selection) {
    const config = JSON.parse(option.dataset.variantMap);
    const chosen = selection.get(config.step);
    const entry = chosen && config.map[[...chosen][0]];
    const input = option.querySelector('.config-option__input');
    if (!entry) return;
    const qty = Number(input.dataset.qty) || 1;
    input.dataset.variant = entry.id;
    input.dataset.price = entry.price * qty;
    input.dataset.bonus = 0;
    input.dataset.label = `${input.dataset.baseLabel} (${entry.title})`;
    input.disabled = !entry.available;
    const text = option.querySelector('[data-config-text]');
    if (text) text.textContent = entry.title;
    const price = option.querySelector('[data-config-unit-price]');
    if (price) price.textContent = `+ ${this.format.format((entry.price * qty) / 100)}`;
  }

  matches(rule, selection) {
    if (!rule) return true;
    return rule.split(',').every((part) => {
      const [key, values] = part.split('=').map((s) => (s || '').trim().toLowerCase());
      if (!key) return true;
      const chosen = selection.get(key);
      return Boolean(chosen) && values.split('|').some((value) => chosen.has(value.trim()));
    });
  }

  // Gewählte Produkte (sichtbar, angehakt, mit Variante)
  items() {
    const result = [];
    this.steps.forEach((step) => {
      if (step.hidden) return;
      step.querySelectorAll('[data-config-option]').forEach((option) => {
        const input = option.querySelector('.config-option__input');
        if (option.hidden || !input.checked) return;
        const source = option.querySelector('.config-option__variants .chips__input:checked') || input;
        if (!source.dataset.variant) return;
        const count = this.count(option);
        result.push({
          id: Number(source.dataset.variant),
          quantity: (Number(source.dataset.qty) || 1) * count,
          price: (Number(source.dataset.price) || 0) * count,
          bonus: (Number(source.dataset.bonus) || 0) * count,
          label: source.dataset.label,
          optional: step.dataset.layout === 'checks',
        });
      });
    });
    return result;
  }

  // Anzahl aus dem Pop-up (Zubehör), sonst 1; mit `data-qty-from` mal der Zahl aus dem genannten Schritt (z. B. Anzahl der Module)
  count(option) {
    const field = option.querySelector('[data-config-qty-input]');
    let count = Math.max(1, Number(field && field.value) || 1);
    const from = option.dataset.qtyFrom && this.selection && this.selection.get(option.dataset.qtyFrom);
    if (from && from.size) count *= Math.max(1, Number([...from][0]) || 1);
    return count;
  }

  // Beschriftung der Auswahl eines Schritts für Set-Karte und Übersicht, z. B. „2 × Verlängerungskabel 3m“
  labels(step) {
    if (step.hidden) return [];
    return [...step.querySelectorAll('[data-config-option]')]
      .filter((option) => !option.hidden && option.querySelector('.config-option__input').checked)
      .map((option) => {
        const variant = option.querySelector('.config-option__variants .chips__input:checked');
        const label = variant ? variant.dataset.label : option.querySelector('.config-option__input').dataset.label;
        const field = option.querySelector('[data-config-qty-input]');
        const count = Math.max(1, Number(field && field.value) || 1);
        return count > 1 ? `${count} × ${label}` : label;
      });
  }

  base() {
    const select = this.product.querySelector('[data-variant-select]');
    const id = Number(select ? select.value : 0);
    return (this.product.variants || []).find((variant) => variant.id === id) || null;
  }

  /* ---------- Preis ---------- */

  renderPrice() {
    const base = this.base();
    const container = document.getElementById(`Price-${this.sectionId}`);
    if (!base || !container) return;
    const items = this.items();
    const extra = items.reduce((sum, item) => sum + item.price - item.bonus, 0);
    const total = this.format.format((base.price + extra) / 100);

    const current = container.querySelector('.price__current');
    if (current) current.lastChild.textContent = ` ${total}`;
    const compare = container.querySelector('.price__compare');
    if (compare) compare.hidden = extra > 0;
    const sticky = document.getElementById(`StickyPrice-${this.sectionId}`);
    if (sticky) sticky.textContent = total;
    this.product.querySelectorAll('[data-config-total]').forEach((el) => {
      el.textContent = total;
    });
  }

  /* ---------- Warenkorb ---------- */

  // Zeilen für /cart/add.js; null, wenn nichts dazukommt (dann normales Formular)
  cartItems(quantity) {
    const base = this.base();
    const items = this.items();
    if (!base || !items.length) return null;
    const parts = items.filter((item) => !item.optional);
    const extras = items.filter((item) => item.optional);
    const set = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const main = parts.length ? { id: base.id, quantity, properties: { _set: set } } : { id: base.id, quantity };
    // Shopify zeigt zuletzt hinzugefügte Zeilen oben: Zubehör zuerst, das Set zuletzt, damit das Set oben steht
    return [
      ...extras.reverse().map((item) => ({ id: item.id, quantity: item.quantity * quantity })),
      ...parts.map((item) => ({ id: item.id, quantity: item.quantity * quantity, properties: { _set: set, _set_part: '1' } })),
      main,
    ];
  }
}

/**
 * Schritt-Assistent (sections/main-configurator.liquid). Liquid rendert alle Seiten untereinander; das Skript zeigt
 * eine Seite, führt die Leiste „Schritt x von y“, füllt Set-Karte und Übersicht mit der Auswahl und öffnet
 * Pop-up-Schritte (z. B. Ausführung der Balkonhalterung), sobald die auslösende Option gewählt ist.
 * Seiten, deren Schritte alle ausgeblendet sind, werden übersprungen.
 */
class SgConfigWizard {
  constructor(product, configurator) {
    this.product = product;
    this.config = configurator;
    this.pages = [...product.querySelectorAll('[data-wizard-page]')];
    this.popups = [...product.querySelectorAll('[data-wizard-popup]')];
    const texts = product.querySelector('[data-wizard-texts]');
    this.texts = texts ? JSON.parse(texts.textContent) : {};
    this.index = 0;
    // Bis wohin der Kunde gekommen ist: spätere Seiten zeigen ihre Vorauswahl erst, wenn er sie gesehen hat
    this.furthest = 0;
    product.classList.add('is-wizard');

    product.addEventListener('click', (event) => {
      const go = event.target.closest('[data-wizard-go]');
      if (go) {
        event.preventDefault();
        const target = go.dataset.wizardGo;
        if (target === 'next' && !this.complete(this.pages[this.index])) return;
        this.go(target === 'next' ? this.step(1) : target === 'back' ? this.step(-1) : Number(target));
        return;
      }
      const select = event.target.closest('[data-config-hint-select]');
      if (select) {
        const [key, value] = select.dataset.configHintSelect.split('=').map((s) => s.trim());
        const label = product.querySelector(`[data-config-step="${key}"] [data-config-option="${value}"] label`);
        if (label) label.click();
        return;
      }
      // Auch ein erneuter Klick auf die schon gewählte Option öffnet ihr Pop-up wieder
      if (event.target.matches('.config-option__input')) {
        const input = event.target;
        setTimeout(() => this.openPopupFor(input), 0);
      }
    });
    product.addEventListener('config:updated', () => {
      this.product.querySelectorAll('[data-wizard-error]').forEach((error) => {
        if (!error.hidden) error.hidden = !this.missing(error.closest('[data-wizard-page]'));
      });
      this.render();
    });
    this.render();
  }

  // Erster sichtbarer Schritt der Seite mit einer Auswahl (nicht Zubehör), in dem noch nichts gewählt ist
  missing(page) {
    return [...page.querySelectorAll('[data-config-step]:not([hidden])')].find(
      (step) => step.dataset.layout !== 'checks' && !step.querySelector('[data-config-option]:not([hidden]) .config-option__input:checked'),
    );
  }

  // Weiter erst nach einer Auswahl; sonst Hinweis über der Navigation und zum offenen Schritt scrollen
  complete(page) {
    const missing = this.missing(page);
    const error = page.querySelector('[data-wizard-error]');
    if (error) error.hidden = !missing;
    if (missing) missing.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return !missing;
  }

  visiblePages() {
    return this.pages.filter((page) => page.hasAttribute('data-wizard-overview') || page.querySelector('[data-config-step]:not([data-wizard-popup-step]):not([hidden])'));
  }

  step(direction) {
    const visible = this.visiblePages();
    const position = visible.indexOf(this.pages[this.index]);
    const next = visible[Math.min(visible.length - 1, Math.max(0, position + direction))];
    return this.pages.indexOf(next);
  }

  go(index) {
    if (index < 0 || !this.pages[index]) return;
    this.index = index;
    this.render();
    const top = this.product.querySelector('[data-wizard-bar]');
    const header = document.querySelector('.section-header');
    const offset = header ? header.getBoundingClientRect().bottom : 0;
    if (top && top.getBoundingClientRect().top < offset) {
      window.scrollTo({ top: window.scrollY + top.getBoundingClientRect().top - offset - 16, behavior: 'smooth' });
    }
  }

  openPopupFor(input) {
    if (!input.checked) return;
    const stepKey = input.closest('[data-config-step]')?.dataset.configStep;
    this.popups.forEach((dialog) => {
      const step = dialog.querySelector('[data-config-step]');
      const [key, values] = (step?.dataset.showIf || '').split('=').map((s) => (s || '').trim().toLowerCase());
      if (key !== stepKey || !values.split('|').includes(input.value)) return;
      this.config.update();
      if (!dialog.open) dialog.showModal();
    });
  }

  pageValue(page) {
    return [...page.querySelectorAll('[data-config-step]')].flatMap((step) => this.config.labels(step)).join(' · ');
  }

  render() {
    const visible = this.visiblePages();
    if (!visible.includes(this.pages[this.index])) this.index = this.pages.indexOf(visible[0]);
    const current = this.pages[this.index];
    const position = visible.indexOf(current);
    this.furthest = Math.max(this.furthest, this.index);

    this.pages.forEach((page, i) => {
      page.hidden = page !== current;
      const shown = visible.includes(page);
      const done = shown && visible.indexOf(page) < position;
      const value = page.hasAttribute('data-wizard-overview') || i > this.furthest ? '' : this.pageValue(page);
      this.product.querySelectorAll(`[data-wizard-seg="${i}"]`).forEach((seg) => {
        seg.hidden = !shown;
        seg.classList.toggle('is-done', done || page === current);
        seg.classList.toggle('is-current', page === current);
      });
      this.product.querySelectorAll(`[data-wizard-icon="${i}"]`).forEach((icon) => {
        icon.hidden = page !== current;
      });
      this.product.querySelectorAll(`[data-wizard-row="${i}"]`).forEach((row) => {
        row.hidden = !shown;
        row.classList.toggle('is-current', page === current);
        row.classList.toggle('is-open', !value && page !== current);
        const text = row.querySelector('[data-wizard-value]');
        if (text) text.textContent = value || (page === current ? this.texts.choose : row.hasAttribute('data-optional') ? this.texts.optional : this.texts.open);
        const change = row.querySelector('[data-wizard-go]');
        if (change) change.hidden = !value || page === current;
      });
    });

    const name = current.dataset.name || '';
    this.product.querySelectorAll('[data-wizard-count]').forEach((el) => {
      el.textContent = (this.texts.count || '').replace('[current]', position + 1).replace('[total]', visible.length);
    });
    this.product.querySelectorAll('[data-wizard-name]').forEach((el) => {
      el.textContent = name;
    });
    const next = visible[position + 1];
    current.querySelectorAll('[data-wizard-next]').forEach((button) => {
      button.hidden = !next;
      if (next) button.querySelector('span').textContent = (this.texts.next || '').replace('[name]', next.dataset.name || '');
    });
    current.querySelectorAll('[data-wizard-go="back"]').forEach((button) => {
      button.hidden = position === 0;
    });
    const summary = visible
      .filter((page) => !page.hasAttribute('data-wizard-overview') && this.pages.indexOf(page) <= this.furthest)
      .map((page) => this.pageValue(page))
      .filter(Boolean)
      .join(' · ');
    this.product.querySelectorAll('[data-wizard-summary]').forEach((el) => {
      el.textContent = summary || this.texts.empty || '';
    });
  }
}

document.querySelectorAll('sg-product[data-configurator]').forEach((product) => {
  product.configurator = new SgConfigurator(product);
  if (product.hasAttribute('data-config-wizard')) product.wizard = new SgConfigWizard(product, product.configurator);
});
